import type { SubmitEventHandler } from 'react';

import { useCallback, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getTestProps } from '../../lib/helpers';

import Button from '../../components/Button/Button';

import useSelectedSpace from '../../hooks/useSelectedSpace';

import { PlusCircleOutlineIcon } from '../../images/shapes';

import { useAppForm } from '../../form-hooks/form';

const customUIPluginFormDefaultPropAllowedHosts = ['localhost'];

interface CustomUIPluginFormProps {
  /**
   * On submit callback
   */
  onSubmit: (url: string) => Promise<boolean>;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * Allowed host names
   */
  allowedHosts?: string[];
  /**
   * Form test id
   */
  testId?: string;
}

const initialValues = {
  url: '',
};

const CustomUIPluginForm = ({
  onSubmit,
  disabled = false,
  allowedHosts = customUIPluginFormDefaultPropAllowedHosts,
  testId = '',
}: CustomUIPluginFormProps) => {
  const { t } = useTranslation();
  const { space } = useSelectedSpace();

  const inputHelpText = useMemo(() => {
    if (allowedHosts.includes('*')) return null;
    return (
      <Trans i18nKey="Plugins.AllowedHostsTooltip">
        {{ hosts: allowedHosts.join(', ') }}
        <a
          className="underline cursor-pointer"
          href={`/space/upgrade/${space}`}
          target={'_blank'}
          rel="noreferrer"
        >
          tier
        </a>
      </Trans>
    );
  }, [allowedHosts, space]);

  const form = useAppForm({
    defaultValues: initialValues,
    onSubmit: async ({ value, formApi }) => {
      const resetForm = await onSubmit(value.url);
      if (resetForm) {
        formApi.reset();
      }
    },
  });

  const handleSubmit = useCallback<SubmitEventHandler>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      form.handleSubmit();
    },
    [form],
  );

  return (
    <form.AppForm>
      <form
        id={'custom-ui-plugin-form'}
        className="pb-3"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        <div className="flex items-end gap-2">
          <form.AppField name="url">
            {(field) => (
              <field.Input
                label={t('Plugins.CustomPlugin')}
                placeholder={t('Plugins.CustomPluginPlaceholder')}
                additionalClasses={'mt-2'}
                {...getTestProps(testId, 'url', 'testId')}
              />
            )}
          </form.AppField>

          <Button
            type="submit"
            form="custom-ui-plugin-form"
            buttonColor="borderlessBlue"
            disabled={disabled}
            additionalClasses="mb-2"
            {...getTestProps(testId, 'add-custom-plugin', 'testId')}
          >
            <PlusCircleOutlineIcon className="min-w-5 w-5" />
          </Button>
        </div>
        {inputHelpText && (
          <div className="text-sm text-slate-600 dark:text-slate-200 mt-1">
            {inputHelpText}
          </div>
        )}
      </form>
    </form.AppForm>
  );
};

export default CustomUIPluginForm;
